<template>
	<PageLoading :loading="user.loadingProfile" id="fairness">
		<PagePadding>
			<BaseCard class="flex items-center gap-4 p-5">
				<FairIcon class="shadow-gamemode h-11 w-11 rounded-full" />
				<div>
					<div class="text-label-primary text-sm font-medium uppercase">
						{{ user.seedPairGames }}
					</div>
					<div class="text-label-secondary mt-1 text-xs font-medium uppercase">
						{{ t('gamesSeed') }}
					</div>
				</div>
			</BaseCard>
			<SubtitleText class="mt-5">{{ t('currentSeedPair') }}</SubtitleText>
			<BaseCard class="mt-2">
				<div class="p-5">
					<div class="flex items-center gap-2">
						<div
							class="text-label-secondary flex-1 text-xs font-medium uppercase"
						>
							{{ t('serverSeed') }}
						</div>
						<CopyIcon
							class="text-label-tertiary relative h-5 w-5 after:absolute after:-inset-3"
							@click="copy(user.serverSeedHash)"
						/>
					</div>
					<div class="text-label-secondary mt-2 truncate text-sm">
						{{ user.serverSeedHash }}
					</div>
				</div>
				<div class="border-t border-black p-5">
					<div class="text-label-primary text-sm font-medium capitalize">
						{{ t('rotateServerSeed') }}
					</div>
					<div class="text-label-secondary mt-1 text-sm">
						{{ t('rotateToReveal') }}
					</div>
					<BaseButton
						class="mt-4 w-full"
						color="secondary"
						size="lg"
						@click="rotate()"
						:class="{
							'animate-shine-after relative overflow-hidden': blinkRotate
						}"
					>
						{{ t('rotate') }}
					</BaseButton>
				</div>
			</BaseCard>
			<BaseCard class="mt-2">
				<div class="p-5">
					<div class="flex items-center gap-2">
						<div
							class="text-label-secondary flex-1 text-xs font-medium uppercase"
						>
							{{ t('clientSeed') }}
						</div>
						<CopyIcon
							class="text-label-tertiary relative h-5 w-5 after:absolute after:-inset-3"
							@click="copy(user.clientSeed)"
						/>
					</div>
					<div class="text-label-secondary mt-2 truncate text-sm">
						{{ user.clientSeed }}
					</div>
				</div>
				<div class="border-t border-black p-5">
					<div class="text-label-primary text-sm font-medium capitalize">
						{{ t('newClientSeed') }}
					</div>
					<div class="text-label-secondary mt-1 text-sm">
						{{ t('itWillAffect') }}
					</div>
					<BaseCard class="mt-4 flex items-center gap-1.5 px-3 text-base" alt>
						<BaseInput
							id="new-seed"
							v-model="newSeed"
							maxlength="32"
							type="text"
							class="focus:text-label-primary text-label-secondary w-full py-2"
							ref="input"
							@focusout="enter"
							@focus="onFocus"
							@enter="enter"
							@submit="enter"
						/>
						<GenerateIcon
							class="text-label-tertiary relative h-5 w-5 after:absolute after:-inset-3"
							@click="randomSeed"
						/>
					</BaseCard>
					<BaseButton
						class="mt-4 w-full"
						color="secondary"
						size="lg"
						@click="edit()"
					>
						{{ btnText }}
					</BaseButton>
				</div>
			</BaseCard>
		</PagePadding>
	</PageLoading>
</template>
<script setup lang="ts">
import PagePadding from '@/components/PagePadding.vue'
import BaseCard from '@/components/BaseCard.vue'
import FairIcon from '@/assets/icons/fair.svg'
import SubtitleText from '@/components/SubtitleText.vue'
import CopyIcon from '@/assets/icons/copy.svg'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import BaseButton from '@/components/BaseButton.vue'
import BaseInput from '@/components/BaseInput.vue'
import GenerateIcon from '@/assets/icons/generate.svg'
import { useUser } from '@/stores/user'
import PageLoading from '@/components/PageLoading.vue'
import { useToast } from '@/stores/toast'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { sleep } from '@/utils/sleep'

const { t } = useI18n({
	messages: {
		en: {
			gamesSeed: 'Games with current seed pair',
			currentSeedPair: 'Current seed pair',
			serverSeed: 'hashed server seed',
			rotateServerSeed: 'Rotate Server Seed',
			rotateToReveal:
				'Rotate to reveal the non-hashed server seed in your previous games.',
			rotate: 'Rotate',
			clientSeed: 'Client Seed',
			newClientSeed: 'New Client Seed',
			itWillAffect:
				'It will affect the outcome of rolls generated with your server seed.',
			copied: 'Copied to clipboard',
			edit: 'edit',
			save: 'save',
			alphanumeric: 'Client seed must be alphanumeric',
			min: 'Client seed must be at least {0} character long',
			max: 'Client seed must be under {0} characters long'
		},
		ru: {
			gamesSeed: 'Игр с текущей seed-парой',
			currentSeedPair: 'Текущая seed-пара',
			serverSeed: 'Хэшированный сервер-seed',
			rotateServerSeed: 'Поменять сервер-seed',
			rotateToReveal:
				'Поменяйте, чтобы увидеть не хэшированный сервер-seed в твоих предыдущих играх.',
			rotate: 'Поменять',
			clientSeed: 'Клиент-seed',
			newClientSeed: 'Новый клиент-seed',
			itWillAffect:
				'Это повлияет на результат игр, сгенерированных с твоим сервер-seed.',
			copied: 'Скопировано',
			edit: 'изменить',
			save: 'сохранить',
			alphanumeric: 'Клиент-seed должен быть буквенно-цифровым',
			min: 'Клиент-seed должен быть не менее {0} символа длиной',
			max: 'Клиент-seed должен быть не более {0} символов длиной'
		}
	}
})

const user = useUser()
const toast = useToast()

const copy = async (text: string) => {
	try {
		await navigator.clipboard.writeText(text)

		toast.add({
			type: 'success',
			text: t('copied')
		})
	} catch {}
}

const rotating = ref(false)

const rotate = async () => {
	if (rotating.value) {
		return
	}

	rotating.value = true

	await user.rotateServerSeed()

	rotating.value = false
}

const newSeed = ref('')
const seedChanged = ref(false)
const editing = ref(false)

watch(newSeed, () => {
	seedChanged.value = true
})

const randomSeed = () => {
	newSeed.value = user.randomSeed()
	seedChanged.value = true
}

randomSeed()

const input = ref()

const focusInput = () => {
	if (!input.value) {
		return
	}

	input.value.focus()

	if (!newSeed.value) {
		return
	}

	input.value.select()
}

const updatingSeed = ref(false)

const edit = async () => {
	if (!editing.value && !seedChanged.value) {
		editing.value = true
		focusInput()
		return
	}

	const regex = /^[a-zA-Z0-9]+$/
	if (!newSeed.value || !regex.test(newSeed.value)) {
		toast.add({
			type: 'error',
			text: t('alphanumeric')
		})
		focusInput()
		return
	}
	if (newSeed.value.length < 1) {
		toast.add({
			type: 'error',
			text: t('min', [1])
		})
		focusInput()
		return
	}
	if (newSeed.value.length > 32) {
		toast.add({
			type: 'error',
			text: t('max', [32])
		})
		focusInput()
		return
	}

	if (updatingSeed.value) {
		return
	}

	updatingSeed.value = true

	await user.updateClientSeed(newSeed.value)

	updatingSeed.value = false
}

const onFocus = () => {
	editing.value = true
}

const enter = () => {
	editing.value = false
}

const btnText = computed(() => {
	if (editing.value || seedChanged.value) {
		return t('save')
	}

	return t('edit')
})

const route = useRoute()
const blinkRotate = ref(false)

onMounted(async () => {
	if (!route.hash) {
		return
	}

	const el = document.getElementById(route.hash.slice(1))

	if (!el) {
		return
	}

	el.scrollIntoView({ behavior: 'smooth', block: 'start' })

	await sleep(1000)

	if (route.hash === '#fairness') {
		blinkRotate.value = true
	}
})
</script>
